import { Location } from "@angular/common";
import { HttpResponse } from "@angular/common/http";
import { EventEmitter, Injectable } from "@angular/core";
import {
    ActivatedRouteSnapshot,
    Resolve,
    RouterStateSnapshot,
} from "@angular/router";
import { ApiGateway, PageOptions } from "cw-backend";
import { CwUtils } from "cw-utils";
import { BehaviorSubject, Observable } from "rxjs";
import { SnackBarService } from "./../../../../cw-utils/snackbar.service";
import { Request } from "./../../../models/operational/medical/request/request";

@Injectable()
export class RequestProvider implements Resolve<any> {
    onDataChanged: BehaviorSubject<any>;
    routeParams: any;
    state: any;
    document: any;
    request: Request;
    pageOptions: PageOptions;
    currentPage: PageOptions;

    constructor(
        public gw: ApiGateway,
        private location: Location,
        private snackBar: SnackBarService,
    ) {
        this.onDataChanged = new BehaviorSubject({});
    }

    static pageRequestType = new EventEmitter<any>();

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        throw new Error("Method not implemented.");
    }

    getAll(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw.get("request/").subscribe((res: HttpResponse<any>) => {
                resolve(res);
            }, reject);
        });
    }

    getById(id: number): Observable<HttpResponse<any>> {
        if (!id) {
            return null;
        }
        return this.gw.get("request/:id", { id: id });
    }

    getByRequestModelId(id: number): Observable<HttpResponse<any>> {
        if (!id) {
            return null;
        }
        return this.gw.get("request/requestModel/:id", { id: id });
    }

    getRequestByRequestModelId(id: number): Observable<HttpResponse<Request>> {
        if (!id) {
            return null;
        }
        return this.gw.get("request/idRequestModel/:id", { id: id });
    }

    getRequestItemListByRequestModelId(
        id: number,
    ): Observable<HttpResponse<any>> {
        if (!id) {
            return null;
        }
        return this.gw.get("request/requestModelId/:id", { id: id });
    }

    getRequestByRequestModel(
        id: number,
        data: any,
    ): Observable<HttpResponse<any>> {
        if (!id) {
            return null;
        }
        return this.gw.post(`request/requestModelId/request/${id}`, data);
    }

    insert(request): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .post("request/", request)
                .subscribe((response: HttpResponse<any>) => {
                    resolve(response);
                    this.snackBar.successMessage(response.body.message);
                }, reject);
        });
    }

    insertRequestDiagnosis(cidList: any, requestId): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .post(`request/cid/${requestId}`, cidList)
                .subscribe((response: HttpResponse<any>) => {
                    resolve(response);
                }, reject);
        });
    }

    createRequestFromRequestModel(requestData): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .post("request/requestFromRequestModel", requestData)
                .subscribe({
                    next: (response: HttpResponse<any>) => {
                        resolve(response);
                        this.snackBar.successMessage(response.body.message);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    createRequestModelByRequest(
        requestId: number,
        requestModel: any,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .post(`request/${requestId}`, requestModel)
                .subscribe((response: HttpResponse<any>) => {
                    resolve(response);
                    this.snackBar.successMessage(response.body.message);
                }, reject);
        });
    }

    insertResquestItem(request): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .post("request/requestItem", request)
                .subscribe((response: HttpResponse<any>) => {
                    resolve(response);
                    this.snackBar.successMessage(response.body.message);
                }, reject);
        });
    }

    edit(request): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .put("request/", request)
                .subscribe((response: HttpResponse<any>) => {
                    resolve(response);
                    this.snackBar.successMessage(response.body.message);
                }, reject);
        });
    }

    editRequestItem(requestItem): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .put("request/requestItem", requestItem)
                .subscribe((response: HttpResponse<any>) => {
                    resolve(response);
                    this.snackBar.successMessage(response.body.message);
                }, reject);
        });
    }

    delete(id): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .delete(`request/${id}`)
                .subscribe((response: HttpResponse<any>) => {
                    resolve(response);
                    this.snackBar.successMessage(response.body.message);
                }, reject);
        });
    }

    getDeletedByPatient(patientId: number, pageOptions: PageOptions): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .post(`request/deleted/${patientId}/search`, pageOptions)
                .subscribe((response: HttpResponse<any>) => {
                    resolve(response);
                }, reject);
        });
    }

    restoreDeleted(id: number): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .put(`request/restore/${id}`)
                .subscribe((response: HttpResponse<any>) => {
                    resolve(response);
                    this.snackBar.successMessage("Requisição restaurada!");
                }, reject);
        });
    }

    deleteRequestItem(id): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .delete(`request/deleteRequestItem/${id}`)
                .subscribe((response: HttpResponse<any>) => {
                    resolve(response);
                    this.snackBar.successMessage(response.body.message);
                }, reject);
        });
    }

    deleteRequestModel(id): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .delete(`request/deleteRequestModel/${id}`)
                .subscribe((response: HttpResponse<any>) => {
                    resolve(response);
                    this.snackBar.successMessage(response.body.message);
                }, reject);
        });
    }

    deleteRequestDiagnosis(cidId, requestId): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .delete(`request/deleteRequestDiagnosis/${cidId}/${requestId}`)
                .subscribe((response: HttpResponse<any>) => {
                    resolve(response);
                    this.snackBar.successMessage(response.body.message);
                }, reject);
        });
    }

    getPage(pageOptions: PageOptions): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .post("request/search", pageOptions)
                .subscribe((res: HttpResponse<any>) => {
                    //patient/searchForAttendance
                    if (
                        !CwUtils.compare(this.request, res.body.content) &&
                        res.body.content.length > 0
                    ) {
                        this.request = res.body.content;
                        this.onDataChanged.next(this.request);
                    } else {
                        this.request = res.body.content;
                    }
                    resolve(res);
                }, reject);
        });
    }

    getPageByID(
        pageOptions: PageOptions,
        typeRequest,
        idPatient,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .post(`request/search/${idPatient}/${typeRequest}`, pageOptions)
                .subscribe((res: HttpResponse<any>) => {
                    //patient/searchForAttendance
                    if (
                        !CwUtils.compare(this.request, res.body.content) &&
                        res.body.content.length > 0
                    ) {
                        this.request = res.body.content;
                        this.onDataChanged.next(this.request);
                    } else {
                        this.request = res.body.content;
                    }
                    resolve(res);
                }, reject);
        });
    }

    getPageDocumentAndRequestByID(
        pageOptions: PageOptions,
        typeRequest,
        idPatient,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .post(
                    `request/searchRequestAndDocument/${idPatient}/${typeRequest}`,
                    pageOptions,
                )
                .subscribe((res: HttpResponse<any>) => {
                    //patient/searchForAttendance
                    if (
                        !CwUtils.compare(this.request, res.body.content) &&
                        res.body.content.length > 0
                    ) {
                        this.request = res.body.content;
                        this.onDataChanged.next(this.request);
                    } else {
                        this.request = res.body.content;
                    }
                    resolve(res);
                }, reject);
        });
    }

    getPageByTypeRequest(pageOptions: PageOptions, typeRequest): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .post(`request/search/${typeRequest}`, pageOptions)
                .subscribe((res: HttpResponse<any>) => {
                    //patient/searchForAttendance
                    if (
                        !CwUtils.compare(this.request, res.body.content) &&
                        res.body.content.length > 0
                    ) {
                        this.request = res.body.content;
                        this.onDataChanged.next(this.request);
                    } else {
                        this.request = res.body.content;
                    }
                    resolve(res);
                }, reject);
        });
    }

    getPageRequestModel(pageOptions: PageOptions, type: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .post(`request/search/request-model/${type}`, pageOptions)
                .subscribe((res: HttpResponse<any>) => {
                    resolve(res);
                }, reject);
        });
    }

    repeatResquest(
        requestId: number,
        professionalId: number,
        convertRtfToHtml = false,
    ): Promise<any> {
        const conversionOption = convertRtfToHtml
            ? "?convertRtfToHtml=true"
            : "";

        return new Promise((resolve, reject) => {
            this.gw
                .post(
                    `request/repeatRequest/${requestId}/${professionalId}${conversionOption}`,
                )
                .subscribe((response: HttpResponse<any>) => {
                    resolve(response);
                    this.snackBar.successMessage(response.body.message);
                }, reject);
        });
    }

    repeatHistoryDocument(
        documentId: number,
        professionalId: number,
        convertRtfToHtml = false,
    ): Promise<any> {
        const conversionOption = convertRtfToHtml
            ? "?convertRtfToHtml=true"
            : "";

        return new Promise((resolve, reject) => {
            this.gw
                .post(
                    `document/repeatDocument/${documentId}/${professionalId}${conversionOption}`,
                )
                .subscribe((response: HttpResponse<any>) => {
                    resolve(response);
                    this.snackBar.successMessage(response.body.message);
                }, reject);
        });
    }

    createRequestForPatient(requestId: number, patientId: number, professionalId: number, replicate = true): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .post(`request/for-patient/${requestId}/${patientId}/${professionalId}?replicate=${replicate}`, {})
                .subscribe((response: HttpResponse<any>) => {
                    resolve(response);
                }, reject);
        });
    }

    getChildRequests(requestId: number): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .get(`request/children/${requestId}`)
                .subscribe((response: HttpResponse<any>) => {
                    resolve(response);
                }, reject);
        });
    }

    updateNoteRequest(requestId: number, note: string): Promise<any> {
        return new Promise((resolve, reject) => {
            const noteText = note === null || note === undefined ? "" : String(note);
            const normalizedNote = ["null", "undefined"].includes(noteText.trim().toLowerCase())
                ? ""
                : noteText;
            this.gw
                .put(`request/obs/${requestId}`, { note: normalizedNote })
                .subscribe((response: any) => {
                    resolve(response);
                    console.log("🚀 ~ this.gw.put ~ response:", response);
                }, reject);
        });
    }

    updatePrintDateRequest(requestId: number, printDate: boolean): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .put(`request/printDate/${requestId}`, { printDate })
                .subscribe((response: any) => {
                    resolve(response);
                }, reject);
        });
    }

    generatePdf(request): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw.post(`request/pdf`, request).subscribe({
                next: (res: any) => {
                    resolve(res.body);
                },
                error(error) {
                    console.error(error);
                    reject(error);
                },
            });
        });
    }

    sendEmailWithPdfAttachment(request): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw.post(`request/send-mail`, request).subscribe({
                next: (res: any) => {
                    resolve(res.body);
                },
                error(error) {
                    console.error(error);
                    reject(error);
                },
            });
        });
    }

    validateSignature(requestId: number): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw.get(`valid/signature/request/${requestId}/status`).subscribe({
                next: (res: any) => {
                    resolve(res.body);
                },
                error(error) {
                    console.error(error);
                    reject(error);
                },
            });
        });
    }

    generatePdfByGcp(request): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw.post(`request/pdf/gcp`, request).subscribe({
                next: (res: any) => {
                    resolve(res.body);
                },
                error(error) {
                    console.error(error);
                    reject(error);
                },
            });
        });
    }

    editStatus(id: number, active: boolean): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .put(`request/${id}/active/${active}`)
                .subscribe((response: HttpResponse<any>) => {
                    resolve(response);
                    this.snackBar.successMessage(
                        "Status atualizado com sucesso!",
                    );
                }, reject);
        });
    }

    signedPdf(request: any): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw.post(`request/pdf/sign`, request).subscribe({
                next: (res: any) => {
                    resolve(res.body);
                },
                error: reject,
            });
        });
    }
}
